import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { ChartDataPoint } from './hooks/useAttributionData'
import { privatize } from '@/lib/utils/revenueUtils'
import { todayYmd } from './attributionView'

/**
 * Attribution trend (Paper "Attribution · Spent vs earned, paired bars"):
 * one question per day, did the ads earn back what they cost. Spend is the
 * neutral bar, earned is `chart-revenue`, side by side on one dollar axis.
 * Money is bars (DESIGN.md): no lines, no second axis, no visitor volume.
 *
 * `series` drops a side when it has no source: earned only without ads
 * (Stripe only), spend only without Stripe (spend reads a darker grey then).
 * Today's earned bar is drawn dashed: payments land about a day after the
 * click, so it is still counting.
 */

const SPENT_COLOR = 'var(--muted-foreground)'
const EARNED_COLOR = 'var(--chart-revenue)'
const GRID_STROKE = 'var(--chart-grid, rgb(0 0 0 / 7%))'
const AXIS_TICK = { fontSize: 11, fill: 'var(--muted-foreground)' }

const fmtAxis = (v: number) => {
  if (v === 0) return '$0'
  if (v >= 100_000) return `$${Math.round(v / 1000).toLocaleString('en-US')}K`
  return `$${Math.round(v).toLocaleString('en-US')}`
}
const fmtMoney = (v: number) =>
  `$${v >= 100 ? Math.round(v).toLocaleString() : v.toFixed(2).replace(/\.00$/, '')}`

function LegendKey({ color, label, opacity = 1 }: { color: string; label: string; opacity?: number }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-2 shrink-0 rounded-[2px]" style={{ backgroundColor: color, opacity }} />
      <span className="text-xs leading-4 text-muted-foreground">{label}</span>
    </span>
  )
}

type BarShapeProps = {
  x?: number
  y?: number
  width?: number
  height?: number
  payload?: ChartDataPoint
}

/** Earned bar; today's is a 35% fill with a dashed edge and a "Still counting" label. */
function EarnedShape({ x = 0, y = 0, width = 0, height = 0, payload, today }: BarShapeProps & { today: string }) {
  if (height <= 0 || width <= 0) return null
  const counting = payload?.rawDate === today
  const r = Math.min(3, width / 2, height)
  const d = `M${x},${y + height}V${y + r}Q${x},${y} ${x + r},${y}H${x + width - r}Q${x + width},${y} ${x + width},${y + r}V${y + height}Z`
  if (!counting) return <path d={d} fill={EARNED_COLOR} />
  return (
    <g>
      <path d={d} fill={EARNED_COLOR} fillOpacity={0.35} stroke={EARNED_COLOR} strokeDasharray="3 2" />
      <text x={x + width / 2} y={y - 8} textAnchor="middle" fontSize={10} fill="var(--muted-foreground)">
        Still counting
      </text>
    </g>
  )
}

export default function SpendEarnedChart({
  chartData,
  earnedKey,
  earnedLabel,
  spentLabel,
  series = 'both',
  otherRevenueLabel,
  note,
  privacyMode = false,
  today = todayYmd(),
}: {
  chartData: ChartDataPoint[]
  /** attributedRevenue once matches exist, else revenue (labelled so) */
  earnedKey: 'adRevenue' | 'attributedRevenue' | 'revenue' | 'trials' | 'sessions'
  earnedLabel: string
  spentLabel: string
  series?: 'both' | 'earned' | 'spent'
  /** Right-aligned line above the bars; defaults to the still-counting note when today is in range */
  /** When set, adds a third bar for organic/direct/referral revenue (otherRevenue in ChartDataPoint) */
  otherRevenueLabel?: string | null
  note?: string | null
  privacyMode?: boolean
  /** yyyy-MM-dd of today (injected by the states gallery) */
  today?: string
}) {
  // For count-based goals (trials/sessions) format values as numbers, not dollars.
  const OTHER_COLOR = 'color-mix(in oklab, var(--chart-revenue) 45%, transparent)'
  const isCount = earnedKey === 'trials' || earnedKey === 'sessions'
  const showOther = !!otherRevenueLabel && series !== 'earned' && !isCount
  const fmtEarned = isCount
    ? (v: number) => (v === 0 ? '0' : v.toLocaleString())
    : fmtMoney
  const fmtEarnedAxis = isCount
    ? (v: number) => (v === 0 ? '0' : v >= 1000 ? `${Math.round(v/1000)}K` : String(Math.round(v)))
    : fmtAxis
  const showEarned = series !== 'spent'
  const showSpent = series !== 'earned'
  const spentOnly = series === 'spent'
  const includesToday = showEarned && chartData.some((d) => d.rawDate === today)
  const rightNote = note !== undefined
    ? note
    : includesToday
      ? 'Today is still counting: payments land about a day after the click'
      : null

  const TooltipBody = ({ active, payload, label }: { active?: boolean; payload?: any[]; label?: string }) => {
    if (!active || !payload?.length) return null
    const row = payload[0].payload as ChartDataPoint
    const spent = row.spend ?? 0
    const earned = (row[earnedKey] as number) ?? 0
    const ret = showEarned && showSpent && spent > 0 ? earned / spent : null
    // Near-black tooltip (chart system): date muted, values white
    return (
      <div className="min-w-[160px] rounded-[4px] bg-foreground px-2.5 py-2 text-[11px] leading-4 text-background shadow-sm">
        <p className="mb-1 text-background/60">{label}</p>
        {showEarned && (
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="size-2 shrink-0 rounded-[2px]" style={{ backgroundColor: EARNED_COLOR }} />
              Earned
            </span>
            <span className="font-medium tabular-nums">{privatize(fmtEarned(earned), privacyMode)}</span>
          </div>
        )}
        {showSpent && (
          <div className="mt-0.5 flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="size-2 shrink-0 rounded-[2px] bg-background/40" />
              Spent
            </span>
            <span className="font-medium tabular-nums">{privatize(fmtMoney(spent), privacyMode)}</span>
          </div>
        )}
        {showOther && (() => { const other = row.otherRevenue ?? 0; return other > 0 ? (
          <div className="mt-0.5 flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="size-2 shrink-0 rounded-[2px]" style={{ backgroundColor: OTHER_COLOR }} />
              Other
            </span>
            <span className="font-medium tabular-nums">{privatize(fmtMoney(other), privacyMode)}</span>
          </div>
        ) : null })()} 
        {ret != null && !isCount && (
          <div className="mt-0.5 flex items-center justify-between gap-4 text-background/60">
            <span>Return per $1</span>
            <span className="tabular-nums">{privatize(`${ret.toFixed(2)}×`, privacyMode)}</span>
          </div>
        )}
      </div>
    )
  }

  if (chartData.length === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center text-[13px] text-muted-foreground">
        {spentOnly ? 'No ad spend in this range yet.' : showSpent ? 'No spend or revenue in this range yet.' : 'No payments in this range yet.'}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-4">
        {showEarned && <LegendKey color={EARNED_COLOR} label={earnedLabel} />}
        {showOther && <LegendKey color={OTHER_COLOR} label={otherRevenueLabel!} opacity={0.5} />}
        {showSpent && <LegendKey color={SPENT_COLOR} label={spentLabel} opacity={spentOnly ? 0.55 : 0.25} />}
        <span className="flex-1" />
        {rightNote && <span className="hidden text-xs leading-4 text-ui-meta sm:inline">{rightNote}</span>}
      </div>
      <div className="w-full" style={{ height: 228 }}>
        <ResponsiveContainer width="100%" height={228}>
          <BarChart data={chartData} margin={{ top: 20, right: 20, bottom: 0, left: 0 }} barGap={4} barCategoryGap="28%">
            <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
            <XAxis dataKey="date" tickLine={false} axisLine={{ stroke: GRID_STROKE }} tickMargin={10} minTickGap={32} height={28} tick={AXIS_TICK} />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={48}
              tickMargin={6}
              tick={AXIS_TICK}
              tickFormatter={(v) => (privacyMode ? '•••' : fmtEarnedAxis(v))}
              tickCount={5}
            />
            <Tooltip cursor={{ fill: 'var(--muted)', fillOpacity: 0.6 }} content={<TooltipBody />} />
            {showSpent && (
              <Bar
                dataKey="spend"
                name={spentLabel}
                fill={SPENT_COLOR}
                fillOpacity={spentOnly ? 0.55 : 0.25}
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
                isAnimationActive={false}
              />
            )}
            {showOther && (
              <Bar
                dataKey="otherRevenue"
                name={otherRevenueLabel ?? ''}
                fill={OTHER_COLOR}
                fillOpacity={0.5}
                radius={[3, 3, 0, 0]}
                maxBarSize={28}
                isAnimationActive={false}
              />
            )}
            {showEarned && (
              <Bar
                dataKey={earnedKey}
                name={earnedLabel}
                fill={EARNED_COLOR}
                shape={(props: unknown) => <EarnedShape {...(props as BarShapeProps)} today={today} />}
                maxBarSize={series === 'earned' ? 30 : 28}
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
